/**
 * 全局 Footer 组件
 *
 * 展示版权信息 + ICP 备案号 + 公网安备号（含图标）
 * 支持两种视觉变体：
 *   - default：跟随主题色，用于主布局
 *   - light：白色文字 + 透明背景，用于登录页（绿色渐变背景上）
 *
 * 备案号通过 Vite 环境变量注入，便于不同环境配置：
 *   VITE_ICP_NUMBER       — ICP 备案号（必填）
 *   VITE_POLICE_NUMBER    — 公网安备号（选填，为空则不显示）
 *   VITE_POLICE_CODE      — 公网安备查询 code（选填，用于生成查询链接）
 *   VITE_COPYRIGHT_OWNER  — 版权主体名称（选填，默认 "DreDabe"）
 */

/** 公安备案图标（位于 public 目录，直接用绝对路径引用） */
const BEIAN_ICON_URL = '/beian-icon.png'

interface FooterProps {
  variant?: 'default' | 'light'
}

/** 工信部 ICP 备案查询地址 */
const MIIT_URL = 'https://beian.miit.gov.cn/'
/** 公安部公网安备查询地址前缀（需拼接 code 参数） */
const POLICE_URL_PREFIX = 'https://beian.mps.gov.cn/#/query/webSearch?code='

export function Footer({ variant = 'default' }: FooterProps) {
  const icpNumber = import.meta.env.VITE_ICP_NUMBER ?? '粤ICP备2026103717号-1'
  const policeNumber = import.meta.env.VITE_POLICE_NUMBER ?? '粤公网安备44200102446209号'
  const policeCode = import.meta.env.VITE_POLICE_CODE ?? '44200102446209'
  const copyrightOwner = import.meta.env.VITE_COPYRIGHT_OWNER ?? 'DreDabe'

  const isLight = variant === 'light'

  const textColor = isLight
    ? 'rgba(255, 255, 255, 0.75)'
    : 'var(--ant-color-text-tertiary, #94A3B8)'
  const linkColor = isLight
    ? 'rgba(255, 255, 255, 0.9)'
    : 'var(--ant-color-text-secondary, #64748B)'
  const separatorColor = isLight
    ? 'rgba(255, 255, 255, 0.4)'
    : 'var(--ant-color-text-quaternary, #CBD5E1)'

  const linkStyle: React.CSSProperties = {
    color: linkColor,
    textDecoration: 'none',
    transition: 'opacity 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4
  }

  const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.style.opacity = '0.75'
  }
  const handleMouseLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.style.opacity = '1'
  }

  return (
    <footer
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        minHeight: 44,
        padding: '10px 24px',
        flexWrap: 'wrap',
        textAlign: 'center',
        fontSize: 12,
        color: textColor,
        flexShrink: 0,
        ...(isLight
          ? {
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1
            }
          : {
              background: 'var(--ant-color-bg-layout, #F5F5F5)',
              borderTop: '1px solid var(--ant-color-border, #E5E7EB)'
            })
      }}
    >
      <span>© 2026 {copyrightOwner}</span>
      <span style={{ color: separatorColor }}>·</span>
      <a
        href={MIIT_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={linkStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {icpNumber}
      </a>
      {policeNumber && (
        <>
          <span style={{ color: separatorColor }}>·</span>
          <a
            href={`${POLICE_URL_PREFIX}${policeCode}`}
            target="_blank"
            rel="noreferrer"
            style={linkStyle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <img
              src={BEIAN_ICON_URL}
              alt="公安备案图标"
              style={{
                width: 14,
                height: 14,
                display: 'inline-block',
                verticalAlign: 'middle'
              }}
            />
            {policeNumber}
          </a>
        </>
      )}
    </footer>
  )
}
